import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { authStorage } from '~/services/authStorage';
import { IMessage } from '~/types/common';
import styles from '~/styles/AdminPanel/AdminPanel.module.css';

interface IAdminUser {
  id: string;
  name: string;
  gender: string;
  status: string;
}

interface IAdminRoom {
  id: string;
  name: string;
  messageCount: number;
  users: IAdminUser[];
  messages: IMessage[];
  lastMessage: string | null;
}

function getInitial(name: string): string {
  return (name || '?').charAt(0).toUpperCase();
}

function formatTime(isoTime: string): string {
  if (!isoTime) return '';
  try {
    return new Date(isoTime).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function PrivateChats() {
  const { context } = useGlobalContext();
  const navigate = useNavigate();

  const [rooms, setRooms] = useState<IAdminRoom[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selectedRoom = rooms.find(r => r.id === selectedRoomId) ?? null;

  // Scroll na dół gdy przychodzi nowa wiadomość w wybranym pokoju
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedRoom?.messages?.length]);

  const sendAdminEvent = (payload: object) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  };

  const handleCloseRoom = (roomId: string) => {
    if (!window.confirm('Zamknąć ten czat prywatny? Obaj użytkownicy zostaną z niego usunięci.')) return;
    sendAdminEvent({ event: 'adminCloseRoom', roomId });
  };

  const handleKickUser = (roomId: string, user: IAdminUser) => {
    if (!window.confirm(`Usunąć użytkownika ${user.name} z tego czatu prywatnego?`)) return;
    sendAdminEvent({ event: 'adminKickUser', roomId, targetUserId: user.id });
  };

  const handleBanWarning = (user: IAdminUser) => {
    if (!window.confirm(`Wysłać ostrzeżenie o banie do ${user.name}?`)) return;
    sendAdminEvent({ event: 'adminBanWarning', targetUserId: user.id });
  };

  const handleBanUser = (user: IAdminUser) => {
    if (!window.confirm(`Zablokować możliwość logowania użytkownika ${user.name}? Zostanie też wylogowany z czatu.`)) return;
    sendAdminEvent({ event: 'adminBanUser', targetUserId: user.id });
  };

  useEffect(() => {
    const token = authStorage.getAccessToken();
    if (!token) return;

    const ws = new WebSocket(process.env.REACT_APP_WEBSOCKET_SERVER as string);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({ event: 'adminJoin', token }));
    };

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      switch (data.event) {
        case 'adminInit':
          setRooms(data.rooms ?? []);
          break;
        case 'adminNewRoom':
          setRooms(prev => [...prev, data.room]);
          break;
        case 'adminMessage':
          setRooms(prev => prev.map(r => {
            if (r.id !== data.roomId) return r;
            return {
              ...r,
              messages: [...r.messages, data.message],
              messageCount: r.messageCount + 1,
              lastMessage: data.lastMessage,
            };
          }));
          break;
        case 'adminRoomClosed':
          setRooms(prev => prev.filter(r => r.id !== data.roomId));
          setSelectedRoomId(prev => prev === data.roomId ? null : prev);
          break;
        case 'adminRoomUpdated':
          setRooms(prev => prev.map(r => r.id === data.room.id ? { ...r, ...data.room } : r));
          break;
      }
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, []);

  if (!context.authUser || context.authUser.role !== 'administrator') {
    return (
      <div className={styles.accessDenied}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2>Brak dostępu</h2>
        <p>Ta strona jest dostępna tylko dla administratorów.</p>
        <button className={styles.refreshBtn} onClick={() => navigate('/')}>Wróć na stronę główną</button>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate('/admin')} title="Wróć do panelu admina">
          ←
        </button>
        <span className={styles.topBarTitle}>Czaty prywatne · podgląd live</span>
        {rooms.length > 0 && <span className={styles.badge}>{rooms.length}</span>}
        <span className={connected ? styles.liveIndicator : styles.liveIndicatorOff}>
          {connected ? '● Live' : '○ Rozłączono'}
        </span>
      </div>

      <div className={`${styles.mainLayout} ${selectedRoomId ? styles.roomSelected : ''}`}>
        {/* ── Sidebar ── */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h2>Aktywne czaty ({rooms.length})</h2>
          </div>
          <div className={styles.roomList}>
            {rooms.length === 0 ? (
              <div className={styles.emptyState}>
                <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p>Brak aktywnych czatów</p>
                <span>Czaty prywatne pojawią się tutaj gdy użytkownicy zaczną rozmawiać.</span>
              </div>
            ) : (
              rooms.map(room => {
                const [u1, u2] = room.users;
                return (
                  <div
                    key={room.id}
                    className={`${styles.roomItem} ${selectedRoomId === room.id ? styles.active : ''}`}
                    onClick={() => setSelectedRoomId(room.id)}
                  >
                    <div className={styles.avatarsStack}>
                      <div className={styles.avatar}>{getInitial(u1?.name)}</div>
                      {u2 && <div className={styles.avatar}>{getInitial(u2?.name)}</div>}
                    </div>
                    <div className={styles.roomInfo}>
                      <div className={styles.roomUsers}>
                        {u1?.name ?? '?'} ↔ {u2?.name ?? '?'}
                      </div>
                      <div className={styles.roomMeta}>
                        {room.lastMessage
                          ? room.lastMessage.length > 40
                            ? room.lastMessage.slice(0, 40) + '…'
                            : room.lastMessage
                          : 'Brak wiadomości'}
                      </div>
                    </div>
                    <span className={styles.msgCountBadge}>{room.messageCount}</span>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* ── Chat panel ── */}
        {selectedRoom ? (
          <section className={styles.chatPanel}>
            <div className={styles.chatHeader}>
              <button
                className={styles.backBtn}
                onClick={() => setSelectedRoomId(null)}
                title="Wróć do listy"
                style={{ marginRight: 8 }}
              >
                ←
              </button>
              <div className={styles.chatHeaderInfo}>
                <div className={styles.chatHeaderTitle}>
                  {selectedRoom.users[0]?.name ?? '?'} ↔ {selectedRoom.users[1]?.name ?? '?'}
                </div>
                <div className={styles.chatHeaderSub}>
                  {selectedRoom.messageCount} {selectedRoom.messageCount === 1 ? 'wiadomość' : 'wiadomości'} · podgląd administratora
                </div>
              </div>
              <button
                className={styles.adminActionDanger}
                onClick={() => handleCloseRoom(selectedRoom.id)}
                title="Zamknij czat prywatny"
              >
                🚪 Zamknij pokój
              </button>
            </div>

            <div className={styles.adminUsersBar}>
              {selectedRoom.users.map(u => (
                <div key={u.id} className={styles.adminUserCard}>
                  <div className={styles.adminUserName}>
                    <span className={styles.adminUserDot} />
                    {u.name}
                  </div>
                  <div className={styles.adminUserActions}>
                    <button
                      className={styles.adminActionBtn}
                      onClick={() => handleKickUser(selectedRoom.id, u)}
                      title="Usuń z czatu prywatnego"
                    >
                      ❌ Usuń
                    </button>
                    <button
                      className={styles.adminActionBtn}
                      onClick={() => handleBanWarning(u)}
                      title="Wyślij ostrzeżenie o banie"
                    >
                      ⚠️ Ostrzeż
                    </button>
                    <button
                      className={styles.adminActionDanger}
                      onClick={() => handleBanUser(u)}
                      title="Zablokuj możliwość logowania użytkownika"
                    >
                      🚫 Zablokuj logowanie
                    </button>
                  </div>
                </div>
              ))}
              {selectedRoom.users.length === 0 && (
                <div className={styles.adminUsersEmpty}>Brak aktywnych uczestników w tym pokoju.</div>
              )}
            </div>

            <div className={styles.messages}>
              {selectedRoom.messages.length === 0 ? (
                <div className={styles.serverMsg}>
                  <span className={styles.serverMsgInner}>Brak wiadomości w tym pokoju.</span>
                </div>
              ) : (
                selectedRoom.messages.map(msg => {
                  if (!msg.userText && msg.serverText) {
                    return (
                      <div key={msg.id} className={styles.serverMsg}>
                        <span className={styles.serverMsgInner}>{msg.serverText}</span>
                      </div>
                    );
                  }
                  const isFirst = selectedRoom.users[0]?.id === msg.userId;
                  const userClass = isFirst ? styles.isFirstUser : styles.isSecondUser;
                  return (
                    <div
                      key={msg.id}
                      className={`${styles.message} ${userClass} ${msg.isModerated ? styles.moderated : ''}`}
                    >
                      <div className={styles.messageContent}>
                        <div className={styles.userHeaderWrapper}>
                          <div className={styles.messageUser}>{msg.userName}</div>
                        </div>
                        <div className={styles.messageText}>
                          {msg.isModerated ? 'Wiadomość ukryta przez moderatora' : msg.userText}
                        </div>
                        <div className={styles.messageTime}>{formatTime(msg.serverTime)}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </section>
        ) : (
          <section className={styles.chatPanelEmpty}>
            <svg width="56" height="56" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>Wybierz czat z listy po lewej stronie</p>
          </section>
        )}
      </div>
    </div>
  );
}

export default PrivateChats;
